import { Injectable, inject, effect, untracked, Injector, runInInjectionContext } from '@angular/core';
import { GraphQLClientService } from './graphql-client.service';
import {
  UserPreferencesService,
  normaliseMapAesthetic,
  normaliseMapLighting,
  normaliseMapTimeMode,
} from './user-preferences.service';
import { ViewerIdentityService } from './viewer-identity.service';
import { isOk } from '../models/api-result';
import type {
  MapAesthetic,
  MapLighting,
  MapTimeMode,
} from './user-preferences.service';

/**
 * RESPONSIBILITY: Cross-device sync for the four indoor-map THEME preferences
 * — `mapAesthetic`, `mapLighting`, `mapTimeMode`, `accessibleColors`. These
 * persist to localStorage via {@link UserPreferencesService} (per-device);
 * this service makes them follow the signed-in user across devices by talking
 * to the backend store (`myMapPreferences` / `updateMapPreferences`, both
 * `@OptionalAuth` — see rdx-platform-api PR #124).
 *
 * WHY A SEPARATE SERVICE (not folded into UserPreferencesService):
 *   UserPreferencesService injects PerformanceService — half of the known
 *   NG0200 (UPS ↔ PerformanceService) circular-dependency. Adding a GraphQL
 *   client + viewer identity into UPS risks reintroducing that cycle. This
 *   service injects UPS ONE-WAY (sync → UPS, never the reverse) so the
 *   dependency graph stays acyclic.
 *
 * LIFECYCLE:
 *   hydrate()    — call on boot / after sign-in. Pulls the backend values and
 *                  folds them into UPS. Conflict policy: BACKEND WINS. When a
 *                  backend field is empty it falls back to the current
 *                  localStorage value (then to UPS defaults). FIRST-BOOT
 *                  MIGRATION: when the backend has no value but localStorage
 *                  does, the local value is pushed up so the user's existing
 *                  per-device choice seeds their cross-device record.
 *   (write-through) — an effect() watches the four UPS theme signals and
 *                  mirrors every change up via `updateMapPreferences`. The
 *                  echo from hydrate() is suppressed so it never loops.
 *   rehydrate()  — re-pull for a freshly-switched account. The demo customer
 *                  switcher hard-reloads the app (so boot re-runs hydrate on
 *                  its own); this is the explicit hook for any in-place switch.
 *   reset()      — on logout: drop the sync baseline so the next signed-in
 *                  user can't inherit the previous one's write-through, and
 *                  reset UPS to defaults (localStorage-only state).
 *
 * ANONYMOUS / DEMO CALLERS:
 *   When there is no authenticated viewer the service is a no-op — no query,
 *   no mutation. localStorage remains the only store. (The backend resolver is
 *   `@OptionalAuth` and would safely echo defaults for anon callers anyway, but
 *   skipping the round-trip avoids needless network and keeps demo sessions
 *   purely local.)
 */

const MY_MAP_PREFERENCES_QUERY = `
  query myMapPreferences {
    myMapPreferences {
      _id
      mapAesthetic
      mapLighting
      mapTimeMode
      accessibleColors
      updatedAt
    }
  }
`;

const UPDATE_MAP_PREFERENCES_MUTATION = `
  mutation updateMapPreferences($input: UpdateMapPreferencesInput!) {
    updateMapPreferences(input: $input) {
      _id
      mapAesthetic
      mapLighting
      mapTimeMode
      accessibleColors
      updatedAt
    }
  }
`;

/** Shape of the backend `MapUserPreferencesType` (theme fields only). */
interface MapPreferencesDto {
  _id?: string;
  mapAesthetic?: string | null;
  mapLighting?: string | null;
  mapTimeMode?: string | null;
  accessibleColors?: boolean | null;
  updatedAt?: string | null;
}

/** The four theme keys this service syncs — nothing else. */
interface ThemePrefs {
  mapAesthetic: MapAesthetic;
  mapLighting: MapLighting;
  mapTimeMode: MapTimeMode;
  accessibleColors: boolean;
}

@Injectable({ providedIn: 'root' })
export class MapPreferencesSyncService {
  private readonly graphql = inject(GraphQLClientService);
  private readonly prefs = inject(UserPreferencesService);
  private readonly identity = inject(ViewerIdentityService);
  private readonly injector = inject(Injector);

  /**
   * True once a hydrate() has folded backend values into UPS. The write-through
   * effect stays dormant until then, so the initial UPS-signal reads (and the
   * hydrate echo itself) never round-trip back to the server.
   */
  private hydrated = false;

  /** Tear-down handle for the write-through effect (started lazily on hydrate). */
  private writeThroughRef: { destroy(): void } | null = null;

  /** Last value pushed to / pulled from the backend — dedupes the effect so we
   *  don't re-send an unchanged blob (e.g. when an unrelated UPS pref changes). */
  private lastSynced: ThemePrefs | null = null;

  /**
   * Whether the current viewer is a real, authenticated user. Anonymous / demo
   * sessions (null viewer or one with no `_id`) are localStorage-only.
   */
  private isAuthenticated(): boolean {
    return !!this.identity.viewer()?._id;
  }

  /** Snapshot the four theme keys from UPS. */
  private currentTheme(): ThemePrefs {
    const p = this.prefs.snapshot();
    return {
      mapAesthetic: p.mapAesthetic,
      mapLighting: p.mapLighting,
      mapTimeMode: p.mapTimeMode,
      accessibleColors: p.accessibleColors,
    };
  }

  private sameTheme(a: ThemePrefs | null, b: ThemePrefs | null): boolean {
    if (!a || !b) return a === b;
    return (
      a.mapAesthetic === b.mapAesthetic &&
      a.mapLighting === b.mapLighting &&
      a.mapTimeMode === b.mapTimeMode &&
      a.accessibleColors === b.accessibleColors
    );
  }

  /**
   * Pull backend theme prefs and fold them into UPS. Conflict policy:
   * backend wins per-field; empty backend field → keep current local value.
   * FIRST-BOOT MIGRATION: if the backend has NO value for a field but local
   * does (i.e. it differs from / is set vs the server), push local up so the
   * existing per-device choice seeds the cross-device record.
   *
   * Idempotent and safe to call repeatedly. No-op for anonymous viewers.
   */
  async hydrate(): Promise<void> {
    if (!this.isAuthenticated()) {
      // Anon/demo — localStorage only. Still arm the write-through guard so a
      // later sign-in (without a reload) starts syncing once hydrate re-runs.
      this.hydrated = false;
      return;
    }

    const local = this.currentTheme();

    const res = await this.graphql.queryResult<MapPreferencesDto>(
      MY_MAP_PREFERENCES_QUERY,
      undefined,
      'network-only',
    );

    // On failure: leave localStorage untouched, don't arm write-through (so a
    // transient error never clobbers the user's local theme with defaults).
    if (!isOk(res)) {
      return;
    }

    const remote = res.data;
    const merged: Partial<ThemePrefs> = {};
    const migrate: Record<string, string | boolean> = {};

    // The backend store is opaque (no server-side enum validation), so remote
    // values can be legacy ids from older builds ('dark', 'light',
    // 'realistic') or unknown strings from other clients. Normalise on the
    // way in — an un-normalised id would be silently rejected by the map
    // engine and the theme would appear stuck (the "black walls" bug). Three
    // outcomes per field:
    //   valid          → apply as-is
    //   legacy         → apply the migrated id AND push it up (repair the record)
    //   unknown / null → keep local AND push local up (seed / repair)

    // mapAesthetic
    const remoteAesthetic = normaliseMapAesthetic(remote?.mapAesthetic);
    if (remoteAesthetic) {
      merged.mapAesthetic = remoteAesthetic;
      if (remoteAesthetic !== remote?.mapAesthetic) {
        migrate['mapAesthetic'] = remoteAesthetic;
      }
    } else {
      migrate['mapAesthetic'] = local.mapAesthetic;
    }
    // mapLighting
    const remoteLighting = normaliseMapLighting(remote?.mapLighting);
    if (remoteLighting) {
      merged.mapLighting = remoteLighting;
      if (remoteLighting !== remote?.mapLighting) {
        migrate['mapLighting'] = remoteLighting;
      }
    } else {
      migrate['mapLighting'] = local.mapLighting;
    }
    // mapTimeMode
    const remoteTimeMode = normaliseMapTimeMode(remote?.mapTimeMode);
    if (remoteTimeMode) {
      merged.mapTimeMode = remoteTimeMode;
      if (remoteTimeMode !== remote?.mapTimeMode) {
        migrate['mapTimeMode'] = remoteTimeMode;
      }
    } else {
      migrate['mapTimeMode'] = local.mapTimeMode;
    }
    // accessibleColors (boolean — null/undefined means unset on the server)
    if (remote?.accessibleColors != null) {
      merged.accessibleColors = remote.accessibleColors;
    } else {
      migrate['accessibleColors'] = local.accessibleColors;
    }

    // Backend wins: apply the remote values into UPS (localStorage). This
    // happens BEFORE the write-through effect is armed so it never echoes.
    if (Object.keys(merged).length > 0) {
      this.prefs.update(merged);
    }

    // First-boot migration: push the local values for fields the server
    // doesn't yet have, so the cross-device record is seeded from this device.
    if (Object.keys(migrate).length > 0) {
      // Fire-and-forget — a migration failure is non-fatal (localStorage is
      // still authoritative locally and the next change will retry the push).
      this.pushUpdate(migrate).catch(() => {});
    }

    // Record the now-authoritative theme and arm write-through.
    this.lastSynced = this.currentTheme();
    this.armWriteThrough();
    this.hydrated = true;
  }

  /**
   * Re-pull for a freshly-switched account. The demo customer switcher does a
   * hard reload, so boot re-runs hydrate() on its own; this exists for any
   * in-place switch path and resets the baseline first.
   */
  async rehydrate(): Promise<void> {
    this.hydrated = false;
    this.lastSynced = null;
    await this.hydrate();
  }

  /**
   * Logout: drop the sync baseline and stop write-through so the next user
   * can't inherit this session's syncing, and reset UPS to defaults (the
   * localStorage-only baseline a signed-out viewer should see).
   */
  reset(): void {
    this.dropSyncBaseline();
    this.prefs.reset();
  }

  /**
   * Customer switch: drop the sync baseline WITHOUT wiping local preferences.
   *
   * `reset()` above is the LOGOUT path, and it resets UserPreferences to
   * DEFAULTS — which includes `onboardingComplete: false`. The customer
   * switch called it, so the same signed-in person got the first-run tour
   * again on every switch and lost their theme, lighting, accessibility and
   * performance choices with it. A switch is not a logout: the viewer is the
   * same human on the same device, and only the SYNCED baseline is
   * customer-scoped.
   *
   * Dropping the baseline is enough — the next `hydrate()` pulls the new
   * account's synced theme and overlays it, which is the "don't migrate the
   * old account's local theme up" behaviour the switch actually wanted.
   */
  dropSyncBaseline(): void {
    this.hydrated = false;
    this.lastSynced = null;
    this.writeThroughRef?.destroy();
    this.writeThroughRef = null;
  }

  /**
   * Arm (once) the effect that mirrors UPS theme changes up to the backend.
   * Reads the four theme signals so Angular tracks them; on every change it
   * pushes the sparse delta. Guarded by `hydrated` + `lastSynced` dedupe so the
   * hydrate echo and unrelated-pref churn never round-trip.
   */
  private armWriteThrough(): void {
    if (this.writeThroughRef) return;

    try {
      this.createWriteThroughEffect();
    } catch (e) {
      // Creating the effect requires a ChangeDetectionScheduler in the
      // injection context. In the real app that's always present; if it ever
      // isn't (e.g. an exotic test harness), write-through is simply disabled —
      // localStorage still holds every change, so the user never loses a theme,
      // only the cross-device push. Never let this break boot hydration.
      console.warn('[MapPreferencesSync] write-through effect unavailable:', e);
    }
  }

  /** Create the UPS→backend mirroring effect. Split out so it can be stubbed in
   *  isolated specs that lack a change-detection scheduler. */
  private createWriteThroughEffect(): void {
    runInInjectionContext(this.injector, () => {
      this.writeThroughRef = effect(() => {
        // Track the four theme signals.
        const next: ThemePrefs = {
          mapAesthetic: this.prefs.mapAesthetic(),
          mapLighting: this.prefs.mapLighting(),
          mapTimeMode: this.prefs.mapTimeMode(),
          accessibleColors: this.prefs.accessibleColors(),
        };

        untracked(() => {
          if (!this.hydrated) return;
          if (!this.isAuthenticated()) return;
          if (this.sameTheme(next, this.lastSynced)) return;

          this.lastSynced = next;
          this.pushUpdate({
            mapAesthetic: next.mapAesthetic,
            mapLighting: next.mapLighting,
            mapTimeMode: next.mapTimeMode,
            accessibleColors: next.accessibleColors,
          }).catch(() => {
            // Network/server hiccup — localStorage already holds the value, so
            // the user keeps their choice locally; the next change retries.
          });
        });
      });
    });
  }

  /** Send a sparse update to the backend. No-op for anonymous viewers. */
  private async pushUpdate(
    input: Record<string, string | boolean>,
  ): Promise<void> {
    if (!this.isAuthenticated()) return;
    if (Object.keys(input).length === 0) return;
    await this.graphql.mutateResult<MapPreferencesDto>(
      UPDATE_MAP_PREFERENCES_MUTATION,
      { input },
    );
  }
}
